import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DirEntry } from '../../lib/api'
import { ApiError } from '../../lib/api'
import { bytes, tanggalPendek, tanggalRelatif } from '../../lib/format'
import { ActionMenu, useTekanLama, usePonsel, type AksiMenu } from '../ActionMenu'
import { Button } from '../Button'
import { Icon, iconForFile } from '../Icon'
import { EmptyState, ErrorState, SkeletonGrid, SkeletonList } from '../StateBlock'
import css from './FileBrowser.module.css'
import { toast } from '../overlay'
import {
  bisaDipratinjau,
  bisaUnduhJpeg,
  jenisPratinjau,
  tampilanTerbaik,
  urutkan,
  type ArahUrut,
  type KolomUrut,
  type SumberBerkas,
} from './source'

/**
 * Penjelajah berkas.
 *
 * Satu komponen untuk share link, ruang pribadi, dan NAS admin. Perbedaan
 * ketiganya diringkas menjadi adaptor `SumberBerkas`, sehingga seluruh
 * interaksi — pengurutan, seleksi rentang, menu konteks, tekan-lama,
 * pratinjau, ZIP — berperilaku identik di mana pun dipakai.
 */

type Tampilan = 'daftar' | 'kisi'

/** Jumlah item yang dirender sebelum menunggu pengguna menggulir. */
const BATCH = 80

interface Props {
  sumber: SumberBerkas
  dir: string
  onPindahDir: (dir: string) => void
  /** Daftar isinya tidak ikut dikirim di sini — itu tugas onDaftarTampil. */
  onPratinjau: (item: DirEntry) => void
  /** Aksi tambahan per item, disisipkan ke menu konteks. */
  aksiTambahan?: (item: DirEntry) => AksiMenu[]
  /** Dipanggil saat seleksi berubah, agar halaman induk bisa menampilkan aksi massal. */
  onSeleksiBerubah?: (terpilih: DirEntry[]) => void
  /**
   * Dipanggil setiap daftar yang tampil berubah — termasuk sekali setelah
   * pemuatan pertama, sebelum pengguna menyentuh apa pun.
   *
   * Halaman induk memerlukannya agar pratinjau bisa dibuka langsung dari URL
   * (`?preview=<path>`). Sebelumnya daftar itu hanya dikirim lewat
   * onPratinjau, yaitu HANYA ketika ada berkas yang diklik; akibatnya membuka
   * atau memuat ulang alamat pratinjau menampilkan foldernya saja tanpa
   * penampil, padahal alamat itu ada justru supaya bisa dibagikan.
   */
  onDaftarTampil?: (items: DirEntry[]) => void
  /** Ditempel di bawah bilah alat — misalnya panel unggah. */
  slotAtas?: React.ReactNode
  /** Penanda yang memaksa muat ulang, mis. setelah unggah selesai. */
  penyegar?: number
}

export function FileBrowser({
  sumber,
  dir,
  onPindahDir,
  onPratinjau,
  aksiTambahan,
  onSeleksiBerubah,
  onDaftarTampil,
  slotAtas,
  penyegar = 0,
}: Props) {
  const [items, setItems] = useState<DirEntry[]>([])
  const [memuat, setMemuat] = useState(true)
  const [galat, setGalat] = useState<{ pesan: string; detail?: string } | null>(null)

  const [cari, setCari] = useState('')
  const [tampilan, setTampilan] = useState<Tampilan>(() => bacaPref(sumber.id, 'tampilan', 'daftar'))
  const [kolom, setKolom] = useState<KolomUrut>(() => bacaPref(sumber.id, 'kolom', 'nama'))
  const [arah, setArah] = useState<ArahUrut>(() => bacaPref(sumber.id, 'arah', 'naik'))

  const [terpilih, setTerpilih] = useState<Set<string>>(new Set())
  const [terlihat, setTerlihat] = useState(BATCH)
  const [menu, setMenu] = useState<{ item: DirEntry; titik: { x: number; y: number } } | null>(null)

  const jangkarSeleksi = useRef<string | null>(null)
  const tampilanDipilihManual = useRef(false)
  const ponsel = usePonsel()

  // ── Muat isi direktori ───────────────────────────────────────────────────
  useEffect(() => {
    const ac = new AbortController()
    setMemuat(true)
    setGalat(null)

    sumber
      .daftar(dir, ac.signal)
      .then((hasil) => {
        setItems(hasil)
        setTerlihat(BATCH)
        // Tebak tampilan terbaik hanya bila pengguna belum memilih sendiri —
        // folder foto dibuka sebagai kisi, folder dokumen sebagai daftar.
        if (!tampilanDipilihManual.current && !bacaPrefAda(sumber.id, 'tampilan')) {
          setTampilan(tampilanTerbaik(hasil))
        }
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const pesan = err instanceof ApiError ? err.message : 'Tidak dapat memuat isi folder.'
        setGalat({ pesan, detail: err instanceof Error ? err.stack : undefined })
      })
      .finally(() => {
        if (!ac.signal.aborted) setMemuat(false)
      })

    return () => ac.abort()
  }, [sumber, dir, penyegar])

  // Seleksi sengaja TIDAK dibersihkan saat pindah folder: memilih berkas dari
  // beberapa folder lalu mengunduhnya sekaligus adalah alur yang nyata, dan
  // versi lama kehilangannya setiap kali pengguna masuk subfolder.
  useEffect(() => {
    jangkarSeleksi.current = null
  }, [dir])

  const tampil = useMemo(() => {
    const kata = cari.trim().toLowerCase()
    const disaring = kata ? items.filter((i) => i.name.toLowerCase().includes(kata)) : items
    return urutkan(disaring, kolom, arah)
  }, [items, cari, kolom, arah])

  const terpilihItems = useMemo(
    () => tampil.filter((i) => terpilih.has(i.path)),
    [tampil, terpilih],
  )

  useEffect(() => {
    onSeleksiBerubah?.(terpilihItems)
  }, [terpilihItems, onSeleksiBerubah])

  useEffect(() => {
    onDaftarTampil?.(tampil)
  }, [tampil, onDaftarTampil])

  // ── Render bertahap ──────────────────────────────────────────────────────
  const sentinelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || terlihat >= tampil.length) return

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setTerlihat((n) => n + BATCH)
      },
      // Dimuat 400 px sebelum terlihat, sehingga penambahan terasa mulus
      // dan bukan sebagai jeda saat menggulir.
      { rootMargin: '400px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [terlihat, tampil.length])

  // ── Seleksi ──────────────────────────────────────────────────────────────
  const toggle = useCallback(
    (item: DirEntry, shift: boolean) => {
      setTerpilih((lama) => {
        const baru = new Set(lama)

        // Shift memilih rentang atas URUTAN YANG SEDANG TAMPIL, bukan urutan
        // mentah dari server — itu yang dilihat pengguna di layar.
        if (shift && jangkarSeleksi.current) {
          const a = tampil.findIndex((i) => i.path === jangkarSeleksi.current)
          const b = tampil.findIndex((i) => i.path === item.path)
          if (a !== -1 && b !== -1) {
            for (let i = Math.min(a, b); i <= Math.max(a, b); i++) {
              const p = tampil[i]?.path
              if (p) baru.add(p)
            }
            return baru
          }
        }

        if (baru.has(item.path)) baru.delete(item.path)
        else baru.add(item.path)
        jangkarSeleksi.current = item.path
        return baru
      })
    },
    [tampil],
  )

  const pilihSemua = () => setTerpilih(new Set(tampil.map((i) => i.path)))
  const kosongkan = () => {
    setTerpilih(new Set())
    jangkarSeleksi.current = null
  }

  // ── Aksi ─────────────────────────────────────────────────────────────────
  const buka = (item: DirEntry) => {
    if (item.is_dir) {
      onPindahDir(item.path)
      return
    }
    if (bisaDipratinjau(item)) {
      onPratinjau(item)
      return
    }
    if (sumber.kemampuan.bolehUnduh) window.location.href = sumber.urlUnduh(item.path)
  }

  const aksiUntuk = (item: DirEntry): AksiMenu[] => {
    const daftar: AksiMenu[] = []

    if (item.is_dir) {
      daftar.push({ id: 'buka', label: 'Buka folder', icon: 'folderOpen', onSelect: () => onPindahDir(item.path) })
    } else if (bisaDipratinjau(item)) {
      daftar.push({
        id: 'pratinjau',
        label: 'Pratinjau',
        icon: 'eye',
        onSelect: () => onPratinjau(item),
      })
    }

    if (sumber.kemampuan.bolehUnduh && !item.is_dir) {
      daftar.push({
        id: 'unduh',
        label: 'Unduh',
        icon: 'download',
        onSelect: () => {
          window.location.href = sumber.urlUnduh(item.path)
        },
      })
    }

    /*
     * Berkas .HIF dari kamera Sony memakai wadah HEIF, dan Photoshop versi
     * lama menolak membukanya. Konversinya disediakan di sini supaya berkasnya
     * langsung bisa dipakai tanpa perlu alat tambahan.
     */
    if (sumber.kemampuan.bolehUnduh && sumber.urlJpeg && bisaUnduhJpeg(item)) {
      daftar.push({
        id: 'unduh-jpeg',
        label: 'Unduh sebagai JPEG',
        icon: 'image',
        onSelect: () => {
          toast.info('Mengonversi ke JPEG, mohon tunggu sebentar.')
          window.location.href = sumber.urlJpeg?.(item.path) ?? '#'
        },
        hint: 'Resolusi penuh, untuk perangkat lunak yang belum mengenal HIF',
      })
    }

    if (sumber.kemampuan.bolehZip && item.is_dir) {
      daftar.push({
        id: 'zip-folder',
        label: 'Unduh folder sebagai ZIP',
        icon: 'fileArchive',
        onSelect: () => unduhFolderZip(item),
        hint: 'Seluruh isi folder ini',
      })
    }

    return [...daftar, ...(aksiTambahan?.(item) ?? [])]
  }

  /**
   * Unduh seluruh isi sebuah folder.
   * Endpoint ZIP menerima daftar berkas, bukan folder, jadi isinya dibaca
   * lebih dulu. Ini yang membuat pengunjung share link akhirnya bisa
   * mengunduh satu folder utuh — sesuatu yang mustahil di versi lama.
   */
  const unduhFolderZip = async (folder: DirEntry) => {
    try {
      const isi = await sumber.daftar(folder.path)
      const berkas = isi.filter((i) => !i.is_dir).map((i) => i.path)
      if (berkas.length === 0) return
      sumber.unduhZip(berkas)
    } catch {
      // Galat sudah ditampilkan lewat lapisan api; di sini cukup tidak
      // melakukan apa-apa daripada menampilkan dua pesan sekaligus.
    }
  }

  const gantiUrut = (k: KolomUrut) => {
    if (k === kolom) {
      const arahBaru = arah === 'naik' ? 'turun' : 'naik'
      setArah(arahBaru)
      tulisPref(sumber.id, 'arah', arahBaru)
    } else {
      setKolom(k)
      setArah('naik')
      tulisPref(sumber.id, 'kolom', k)
      tulisPref(sumber.id, 'arah', 'naik')
    }
  }

  const gantiTampilan = (t: Tampilan) => {
    tampilanDipilihManual.current = true
    setTampilan(t)
    tulisPref(sumber.id, 'tampilan', t)
  }

  return (
    <div className={css.wrap}>
      <Breadcrumb labelAkar={sumber.labelAkar} dir={dir} onPindah={onPindahDir} />

      <div className={css.toolbar}>
        <div className={css.search}>
          <span className={css.searchIcon}>
            <Icon name="search" size={16} />
          </span>
          <input
            type="search"
            value={cari}
            onChange={(e) => setCari(e.target.value)}
            placeholder="Cari di folder ini"
            aria-label="Cari berkas di folder ini"
          />
        </div>

        <div className={css.toolbarSpacer} />

        <div className={css.segmented} role="group" aria-label="Tampilan">
          <button
            type="button"
            className={`${css.segment} ${tampilan === 'daftar' ? css.active : ''}`}
            onClick={() => gantiTampilan('daftar')}
            aria-pressed={tampilan === 'daftar'}
            aria-label="Tampilan daftar"
          >
            <Icon name="list" size={17} />
          </button>
          <button
            type="button"
            className={`${css.segment} ${tampilan === 'kisi' ? css.active : ''}`}
            onClick={() => gantiTampilan('kisi')}
            aria-pressed={tampilan === 'kisi'}
            aria-label="Tampilan kisi"
          >
            <Icon name="grid" size={17} />
          </button>
        </div>
      </div>

      {slotAtas}

      {terpilih.size > 0 && (
        <div className={css.selectionBar}>
          <span className={css.selectionCount}>{terpilih.size} dipilih</span>
          <Button size="sm" variant="ghost" onClick={pilihSemua}>
            Pilih semua
          </Button>
          {sumber.kemampuan.bolehZip && (
            <Button
              size="sm"
              variant="primary"
              icon="download"
              onClick={() =>
                sumber.unduhZip(terpilihItems.filter((i) => !i.is_dir).map((i) => i.path))
              }
              disabled={terpilihItems.every((i) => i.is_dir)}
            >
              Unduh ZIP
            </Button>
          )}
          <Button size="sm" variant="ghost" icon="close" onClick={kosongkan} aria-label="Batalkan seleksi" />
        </div>
      )}

      <PanelReadme sumber={sumber} items={items} />

      <div className={css.panel}>
        {memuat ? (
          tampilan === 'kisi' ? <SkeletonGrid /> : <SkeletonList />
        ) : galat ? (
          <ErrorState
            message={galat.pesan}
            {...(galat.detail ? { details: galat.detail } : {})}
            onRetry={() => onPindahDir(dir)}
          />
        ) : tampil.length === 0 ? (
          cari ? (
            <EmptyState
              icon="search"
              title="Tidak ada yang cocok"
              message={`Tidak ada berkas atau folder bernama "${cari}" di folder ini.`}
              action={
                <Button variant="secondary" onClick={() => setCari('')}>
                  Hapus pencarian
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon="folderOpen"
              title="Folder ini kosong"
              message={
                sumber.kemampuan.bolehUnggah
                  ? 'Unggah berkas atau buat folder baru untuk mulai mengisinya.'
                  : 'Belum ada berkas di dalam folder ini.'
              }
            />
          )
        ) : tampilan === 'kisi' ? (
          <div className={css.grid}>
            {tampil.slice(0, terlihat).map((item) => (
              <KartuBerkas
                key={item.path}
                item={item}
                sumber={sumber}
                terpilih={terpilih.has(item.path)}
                onToggle={(shift) => toggle(item, shift)}
                onBuka={() => buka(item)}
                onMenu={(titik) => setMenu({ item, titik })}
                ponsel={ponsel}
              />
            ))}
          </div>
        ) : (
          <>
            <div className={css.headerRow}>
              <span />
              <HeaderUrut label="Nama" k="nama" aktif={kolom} arah={arah} onKlik={gantiUrut} />
              <HeaderUrut label="Ukuran" k="ukuran" aktif={kolom} arah={arah} onKlik={gantiUrut} />
              <HeaderUrut label="Diubah" k="tanggal" aktif={kolom} arah={arah} onKlik={gantiUrut} />
              <span />
            </div>
            {tampil.slice(0, terlihat).map((item) => (
              <BarisBerkas
                key={item.path}
                item={item}
                sumber={sumber}
                terpilih={terpilih.has(item.path)}
                onToggle={(shift) => toggle(item, shift)}
                onBuka={() => buka(item)}
                onMenu={(titik) => setMenu({ item, titik })}
                ponsel={ponsel}
              />
            ))}
          </>
        )}

        {terlihat < tampil.length && (
          <div ref={sentinelRef} className={css.sentinel}>
            Memuat {Math.min(BATCH, tampil.length - terlihat)} item berikutnya…
          </div>
        )}
      </div>

      <ActionMenu
        open={menu !== null}
        onClose={() => setMenu(null)}
        judul={menu?.item.name}
        anchor={menu?.titik ?? null}
        aksi={menu ? aksiUntuk(menu.item) : []}
      />
    </div>
  )
}


/**
 * Baca status tombol Shift dari sebuah peristiwa.
 *
 * React membungkus peristiwa asli di dalam `nativeEvent`; Preact meneruskan
 * peristiwa aslinya apa adanya sehingga properti itu tidak ada. Membacanya
 * langsung akan melempar TypeError — dan itu tidak tertangkap oleh pemeriksa
 * tipe, karena tipenya berasal dari preact/compat yang meniru React.
 */
function shiftDitekan(e: { shiftKey?: boolean; nativeEvent?: unknown }): boolean {
  if (typeof e.shiftKey === 'boolean') return e.shiftKey
  const asli = e.nativeEvent
  return typeof asli === 'object' && asli !== null && 'shiftKey' in asli
    ? Boolean((asli as { shiftKey: unknown }).shiftKey)
    : false
}

/**
 * Panel README.
 *
 * Ditampilkan bila folder memuat berkas bernama readme.md. Isinya dirender
 * sebagai teks biasa, BUKAN lewat innerHTML seperti versi lama — siapa pun
 * yang bisa menaruh berkas di NAS dulu dapat menjalankan skrip di peramban
 * siapa pun yang membuka foldernya.
 */
function PanelReadme({ sumber, items }: { sumber: SumberBerkas; items: DirEntry[] }) {
  const readme = items.find((i) => !i.is_dir && i.name.toLowerCase() === 'readme.md')
  const [isi, setIsi] = useState<string | null>(null)
  const [terbuka, setTerbuka] = useState(false)

  useEffect(() => {
    if (!readme) {
      setIsi(null)
      return
    }
    const ac = new AbortController()
    setTerbuka(false)
    fetch(sumber.urlLihat(readme.path), { credentials: 'same-origin', signal: ac.signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => setIsi(t.slice(0, 64 * 1024)))
      .catch(() => setIsi(null))
    return () => ac.abort()
  }, [sumber, readme])

  if (!readme || !isi) return null

  const panjang = isi.split('\n').length > 8 || isi.length > 600

  return (
    <div className={css.readme}>
      <div className={css.readmeKepala}>
        <Icon name="fileText" size={15} />
        README.md
      </div>
      <div className={terbuka || !panjang ? css.readmeIsi : css.readmeIsiTerpotong}>{isi}</div>
      {panjang && (
        <button type="button" className={css.readmeTombol} onClick={() => setTerbuka((v) => !v)}>
          {terbuka ? 'Sembunyikan' : 'Lihat selengkapnya'}
          <Icon name={terbuka ? 'chevronUp' : 'chevronDown'} size={14} />
        </button>
      )}
    </div>
  )
}

// ── Bagian-bagian ──────────────────────────────────────────────────────────

function Breadcrumb({
  labelAkar,
  dir,
  onPindah,
}: {
  labelAkar: string
  dir: string
  onPindah: (d: string) => void
}) {
  const segmen = dir.split('/').filter(Boolean)

  return (
    <nav className={css.breadcrumb} aria-label="Lokasi folder">
      <button type="button" className={css.crumb} onClick={() => onPindah('')}>
        <Icon name="home" size={15} />
        {labelAkar}
      </button>
      {segmen.map((s, i) => {
        const path = segmen.slice(0, i + 1).join('/')
        const terakhir = i === segmen.length - 1
        return (
          <span key={path} style={{ display: 'contents' }}>
            <Icon name="chevronRight" size={14} className={css.crumbSep} />
            <button
              type="button"
              className={`${css.crumb} ${terakhir ? css.crumbCurrent : ''}`}
              onClick={() => !terakhir && onPindah(path)}
              aria-current={terakhir ? 'location' : undefined}
            >
              {s}
            </button>
          </span>
        )
      })}
    </nav>
  )
}

function HeaderUrut({
  label,
  k,
  aktif,
  arah,
  onKlik,
}: {
  label: string
  k: KolomUrut
  aktif: KolomUrut
  arah: ArahUrut
  onKlik: (k: KolomUrut) => void
}) {
  const ini = aktif === k
  return (
    <button
      type="button"
      className={`${css.headerSort} ${ini ? css.active : ''}`}
      onClick={() => onKlik(k)}
      aria-sort={ini ? (arah === 'naik' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {ini && <Icon name={arah === 'naik' ? 'chevronUp' : 'chevronDown'} size={13} />}
    </button>
  )
}

interface ItemProps {
  item: DirEntry
  sumber: SumberBerkas
  terpilih: boolean
  onToggle: (shift: boolean) => void
  onBuka: () => void
  onMenu: (titik: { x: number; y: number }) => void
  ponsel: boolean
}

function BarisBerkas({ item, sumber, terpilih, onToggle, onBuka, onMenu, ponsel }: ItemProps) {
  const tekan = useTekanLama(onMenu)
  const jenis = jenisPratinjau(item)
  const kelasIkon = item.is_dir ? css.dir : jenis === 'gambar' || jenis === 'video' ? css.media : ''

  return (
    <div
      className={`${css.row} ${terpilih ? css.selected : ''} ${css.clickable}`}
      onClick={(e) => {
        // Ctrl/Cmd-klik menambah ke seleksi tanpa membuka berkasnya.
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault()
          onToggle(false)
          return
        }
        if (e.shiftKey) {
          e.preventDefault()
          onToggle(true)
          return
        }
        onBuka()
      }}
      onContextMenu={(e) => {
        e.preventDefault()
        onMenu({ x: e.clientX, y: e.clientY })
      }}
      {...(ponsel ? tekan : {})}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onBuka()
        }
      }}
    >
      {sumber.kemampuan.bolehPilih ? (
        <input
          type="checkbox"
          className={css.check}
          checked={terpilih}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onToggle(shiftDitekan(e))}
          aria-label={`Pilih ${item.name}`}
        />
      ) : (
        <span />
      )}

      <div className={css.nameCell}>
        <span className={`${css.fileIcon} ${kelasIkon}`}>
          <Icon name={iconForFile(item.ext, item.is_dir)} size={18} />
        </span>
        <span className={css.nameText}>
          <span className={`${css.nameLine} truncate`} style={{ display: 'block' }} title={item.name}>
            {item.name}
          </span>
          <span className={css.metaLine}>
            {item.is_dir ? 'Folder' : bytes(item.size ?? 0)} · {tanggalPendek(item.modified)}
          </span>
        </span>
      </div>

      <span className={css.colSize}>{item.is_dir ? '—' : bytes(item.size ?? 0)}</span>
      <span className={css.colDate}>{tanggalRelatif(item.modified)}</span>

      <span className={css.rowActions}>
        {sumber.kemampuan.bolehBintang && sumber.toggleBintang && (
          <TombolBintang sumber={sumber} item={item} />
        )}
        <Button
          size="sm"
          variant="ghost"
          icon="more"
          aria-label={`Aksi untuk ${item.name}`}
          onClick={(e) => {
            e.stopPropagation()
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
            onMenu({ x: r.right - 232, y: r.bottom + 4 })
          }}
        />
      </span>
    </div>
  )
}

function KartuBerkas({ item, sumber, terpilih, onToggle, onBuka, onMenu, ponsel }: ItemProps) {
  const tekan = useTekanLama(onMenu)
  const [gagalThumb, setGagalThumb] = useState(false)
  const gambar = jenisPratinjau(item) === 'gambar' && !gagalThumb && sumber.urlThumbnail(item.path)

  return (
    <div
      className={`${css.card} ${terpilih ? css.selected : ''}`}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          e.preventDefault()
          onToggle(e.shiftKey)
          return
        }
        onBuka()
      }}
      onContextMenu={(e) => {
        e.preventDefault()
        onMenu({ x: e.clientX, y: e.clientY })
      }}
      {...(ponsel ? tekan : {})}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onBuka()
        }
      }}
    >
      {sumber.kemampuan.bolehPilih && (
        <input
          type="checkbox"
          className={`${css.check} ${css.cardCheck}`}
          checked={terpilih}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onToggle(shiftDitekan(e))}
          aria-label={`Pilih ${item.name}`}
        />
      )}

      <span className={css.cardMore}>
        <Button
          size="sm"
          variant="ghost"
          icon="more"
          aria-label={`Aksi untuk ${item.name}`}
          onClick={(e) => {
            e.stopPropagation()
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
            onMenu({ x: r.right - 232, y: r.bottom + 4 })
          }}
        />
      </span>

      <div className={`${css.thumb} ${item.is_dir ? css.dir : ''}`}>
        {gambar ? (
          <img
            src={gambar}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setGagalThumb(true)}
          />
        ) : (
          <Icon name={iconForFile(item.ext, item.is_dir)} size={34} />
        )}
      </div>

      <div className={`${css.cardName} clamp-2`} title={item.name}>
        {item.name}
      </div>
      <div className={css.cardMeta}>
        {item.is_dir ? 'Folder' : bytes(item.size ?? 0)}
      </div>
    </div>
  )
}

/**
 * Tombol bintang dengan pembaruan optimistik.
 *
 * Status berubah seketika lalu dikembalikan bila server menolak — menunggu
 * balasan server untuk perubahan sekecil ini membuat antarmuka terasa lamban.
 */
function TombolBintang({ sumber, item }: { sumber: SumberBerkas; item: DirEntry }) {
  const [aktif, setAktif] = useState(false)
  const [sibuk, setSibuk] = useState(false)

  return (
    <Button
      size="sm"
      variant={aktif ? 'primary' : 'ghost'}
      icon="star"
      disabled={sibuk}
      aria-label={aktif ? `Hapus ${item.name} dari Tersimpan` : `Simpan ${item.name}`}
      aria-pressed={aktif}
      onClick={async (e) => {
        e.stopPropagation()
        if (!sumber.toggleBintang) return
        const sebelum = aktif
        setAktif(!sebelum)
        setSibuk(true)
        try {
          const hasil = await sumber.toggleBintang(item)
          setAktif(hasil)
        } catch {
          setAktif(sebelum)
        } finally {
          setSibuk(false)
        }
      }}
    />
  )
}

// ── Preferensi ─────────────────────────────────────────────────────────────

function kunciPref(sumberId: string, nama: string): string {
  return `gmsdrive:${sumberId}:${nama}`
}

function bacaPref<T extends string>(sumberId: string, nama: string, bawaan: T): T {
  try {
    return (localStorage.getItem(kunciPref(sumberId, nama)) as T | null) ?? bawaan
  } catch {
    // localStorage bisa dilarang pada mode privat sebagian peramban.
    return bawaan
  }
}

function bacaPrefAda(sumberId: string, nama: string): boolean {
  try {
    return localStorage.getItem(kunciPref(sumberId, nama)) !== null
  } catch {
    return false
  }
}

function tulisPref(sumberId: string, nama: string, nilai: string): void {
  try {
    localStorage.setItem(kunciPref(sumberId, nama), nilai)
  } catch {
    /* diabaikan */
  }
}
